import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import AuthStore from '@portal/env/stores/authStore';
import EventsStore from '@portal/env/stores/eventsStore';
import { useAuth } from './useAuth';
import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import type { Event } from '@portal/types/nexudus/events';
import { queryKeys } from '@portal/ui/hooks/queryKeys';
import { IValueMap } from 'mobx-react/src/types/IValueMap';
import { clientConfig } from 'env/config';
import { fetchAllPages } from '@portal/env/utils/fetchAllPages';
import { fetchEventsPage } from '@portal/env/hooks/useEventsQuery';

export const eventsQueryKeys = {
  upcomingEvents: [...queryKeys.events, 'upcoming'] as const,
  all: [...queryKeys.events, 'all'] as const,
};

export type Predicate<T> = (item: T) => boolean;

export const not =
  <T>(predicate: Predicate<T>): Predicate<T> =>
  (item) =>
    !predicate(item);

export const isNotGymClass: Predicate<Event> = (event) => {
  const { gymEventId } = clientConfig();
  return event.EventCategories.every((category) => category.Id !== gymEventId);
};

export const isFeatured: Predicate<{ ShowInHomeBanner: boolean }> = (item) =>
  item.ShowInHomeBanner;

export const inCategory =
  (categoryId: number | null): Predicate<Event> =>
  (event) =>
    categoryId === null ||
    event.EventCategories.some((category) => category.Id === categoryId);

export const matchesSearch =
  (query: string): Predicate<Event> =>
  (event) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      // Nexudus can omit these despite the types
      (event.Name ?? '').toLowerCase().includes(q) ||
      (event.ShortDescription ?? '').toLowerCase().includes(q)
    );
  };

export const eventsQueryOptions = (
  eventsStore: EventsStore,
  authStore: AuthStore,
) =>
  queryOptions({
    queryKey: eventsQueryKeys.upcomingEvents,
    enabled: authStore.isLoggedIn,
    queryFn: () => eventsStore.loadUpcomingEvents() as Promise<Event[]>,
  });

export const dashboardEventsQueryOptions = (
  eventsStore: EventsStore,
  authStore: AuthStore,
) =>
  queryOptions({
    ...eventsQueryOptions(eventsStore, authStore),
    select: (data) => data.filter(isNotGymClass).slice(0, 3),
  });

const useEventsStores = () => {
  const stores = useContext(MobXProviderContext) as Record<string, unknown>;
  if (!stores?.eventsStore || !stores?.authStore) {
    throw new Error('useEvents hooks must be used within a MobX Provider');
  }
  return {
    eventsStore: stores.eventsStore as EventsStore,
    authStore: stores.authStore as AuthStore,
  };
};

export const useEventsQuery = () => {
  const { eventsStore, authStore } = useEventsStores();
  return useSuspenseQuery(eventsQueryOptions(eventsStore, authStore));
};

export const useDashboardEventsQuery = () => {
  const { eventsStore, authStore } = useEventsStores();
  return useSuspenseQuery(dashboardEventsQueryOptions(eventsStore, authStore));
};

export function useEvents() {
  const stores = useContext(MobXProviderContext) as IValueMap;
  if (!stores?.eventsStore || !stores?.authStore) {
    throw new Error('useEvents must be used within a MobX Provider');
  }
  return { store: stores.eventsStore as EventsStore };
}

const PAGE_SIZE = 50;

type EventCategory = { Id: number; Title: string };

export type AllEvents = {
  events: Event[];
  categories: EventCategory[];
  pastEvents: boolean;
};

export const allEventsQueryOptions = (
  token: string,
  host: string,
  params: { past?: boolean } = {},
) =>
  queryOptions<AllEvents>({
    queryKey: [...eventsQueryKeys.all, params] as const,
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { firstPage, items } = await fetchAllPages({
        fetchPage: (page) =>
          fetchEventsPage(token, host, page, PAGE_SIZE, params.past ?? false),
        getItems: (page) => (page.CalendarEvents ?? []) as Event[],
        pageSize: PAGE_SIZE,
      });
      return {
        events: items,
        categories: (firstPage.Categories ?? []) as EventCategory[],
        pastEvents: Boolean(params.past),
      };
    },
  });

export const useAllEventsQuery = (params?: { past?: boolean }) => {
  const { accessToken, host } = useAuth();
  return useQuery(allEventsQueryOptions(accessToken, host, params));
};
